import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import {
	TabFriendsActiveSvg,
	TabFriendsSvg,
	TabInventoryActiveSvg,
	TabInventorySvg,
	TabProfileSvg,
} from '../../../assets/inventory';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import {
	ACTIVE_FILL,
	INVENTORY_FONT,
	inventoryColors,
	inventoryGlow,
	RADIUS_MD,
	TAB_FILL,
} from './inventoryTheme';

/**
 * Figma "Tab / Modal" (4038:30971) - the Profile / Inventory / Friends row across the modal's
 * header. The file's note: 'Label and icon are baked per variant so both follow the state', and
 * the active state is 'the paired shadow (#FF0000 55% r6 + #FF7200 45% r16)'.
 *
 * Inventory and Friends both have a body behind them and carry an active icon. Profile has no
 * frame in the file yet, so it renders in its resting state and does nothing when pressed.
 */
export type InventoryTabKey = 'profile' | 'inventory' | 'friends';

// The header's "Tabs" frame is 292 wide: three 93.33 tabs with 6 between them.
export const TABS_DESIGN_WIDTH = 292;
const TAB_HEIGHT = 34;
const TAB_GAP = 6;
const ICON_SIZE = 18;
const LABEL_GAP = 6;

// `label` is the English default for `labelKey`.
const TABS: Array<{ key: InventoryTabKey; label: string; labelKey: string }> = [
	{ key: 'profile', label: 'Profile', labelKey: 'T_SITE_PROFILE_PROFILE' },
	{ key: 'inventory', label: 'Inventory', labelKey: 'T_SITE_STORE_INVENTORY' },
	{ key: 'friends', label: 'Friends', labelKey: 'T_SITE_MENU_FRIENDS' },
];

/**
 * The gradient plate behind each tab is absolutely positioned, and CSS paints positioned elements
 * above in-flow siblings regardless of DOM order. React Native Web gives every View (and Text) its
 * own `position: relative`, so those keep painting on top - but an SVGR icon is a bare `<svg>`
 * with no position, which lands underneath the plate and disappears. Wrapping it in a View is what
 * puts it back in the same painting step as the label.
 */
const TabIcon = ({
	tab,
	active,
	size,
}: {
	tab: InventoryTabKey;
	active: boolean;
	size: number;
}) => {
	const Icon =
		tab === 'profile'
			? TabProfileSvg
			: tab === 'friends'
				? active
					? TabFriendsActiveSvg
					: TabFriendsSvg
				: active
					? TabInventoryActiveSvg
					: TabInventorySvg;
	const height =
		tab === 'friends' ? (size * 14) / 16.382 : tab === 'profile' ? size : (size * 16.5) / 18;

	return (
		<View>
			<Icon width={size} height={height} />
		</View>
	);
};

type Props = {
	value: InventoryTabKey;
	onChange?: (tab: InventoryTabKey) => void;
	/**
	 * The row the three tabs share; defaults to the inventory modal's own 292. Figma draws the
	 * profile modal's header (4022:37725) narrower, at 286, but the profile tab now switches to
	 * and from that same inventory panel without it remounting, so it passes 292 too - two
	 * different widths here would read as the tab bar jumping sideways on every switch.
	 */
	rowWidth?: number;
	scale: number;
};

export const InventoryTabs = ({ value, onChange, rowWidth = TABS_DESIGN_WIDTH, scale }: Props) => {
	const { t } = useTranslation();
	const styles = useMemo(() => createStyles(scale, rowWidth), [rowWidth, scale]);
	const activeFillId = useSvgId('inventoryTabActive');
	const restFillId = useSvgId('inventoryTabRest');

	return (
		<View style={styles.row}>
			{TABS.map(tab => {
				const isActive = tab.key === value;

				return (
					<Pressable
						key={tab.key}
						accessibilityRole='tab'
						accessibilityState={{ selected: isActive }}
						onPress={onChange ? () => onChange(tab.key) : undefined}
						style={[styles.tab, isActive ? styles.tabActive : styles.tabRest]}
					>
						<View pointerEvents='none' style={StyleSheet.absoluteFill}>
							<Svg width='100%' height='100%'>
								<Defs>
									<LinearGradient id={activeFillId} x1='0' y1='0' x2='0' y2='1'>
										{ACTIVE_FILL.map(([offset, color]) => (
											<Stop key={offset} offset={offset} stopColor={color} />
										))}
									</LinearGradient>
									<LinearGradient id={restFillId} x1='0' y1='0' x2='0' y2='1'>
										{TAB_FILL.map(([offset, color]) => (
											<Stop key={offset} offset={offset} stopColor={color} />
										))}
									</LinearGradient>
								</Defs>
								<Rect
									x='0'
									y='0'
									width='100%'
									height='100%'
									fill={`url(#${isActive ? activeFillId : restFillId})`}
								/>
							</Svg>
						</View>

						<TabIcon tab={tab.key} active={isActive} size={ICON_SIZE * scale} />
						<Text
							numberOfLines={1}
							style={[styles.label, isActive ? styles.labelActive : styles.labelRest]}
						>
							{t(tab.labelKey, { defaultValue: tab.label })}
						</Text>
					</Pressable>
				);
			})}
		</View>
	);
};

const createStyles = (scale: number, rowWidth: number) =>
	StyleSheet.create({
		row: {
			width: rowWidth * scale,
			height: TAB_HEIGHT * scale,
			flexDirection: 'row',
			gap: TAB_GAP * scale,
		},
		tab: {
			width: ((rowWidth - TAB_GAP * 2) / 3) * scale,
			height: TAB_HEIGHT * scale,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: LABEL_GAP * scale,
			borderRadius: RADIUS_MD * scale,
			borderWidth: Math.max(1, 1 * scale),
			overflow: 'hidden',
		},
		tabRest: {
			borderColor: inventoryColors.border,
		},
		tabActive: {
			borderColor: inventoryColors.borderActive,
			...inventoryGlow.active,
		},
		// Figma "DS/Label/M": Sofia Sans SemiBold 12, line-height 1.2.
		label: {
			fontFamily: INVENTORY_FONT,
			fontSize: 12 * scale,
			fontWeight: '600',
			lineHeight: 12 * 1.2 * scale,
		},
		labelRest: {
			color: inventoryColors.secondary,
		},
		labelActive: {
			color: inventoryColors.primary,
		},
	});

export default InventoryTabs;
